// =============================================================================
// ShelterDeliverySelect.jsx — New Request delivery picker (structured one-offs)
// =============================================================================
// The shelter-side New Request general delivery picker. Same registered-address
// radio-cards as DeliverySelect, but "Different address" opens AddressCaptureModal
// (structured fields + a required name, empty to start) instead of a single free-text
// line — so a one-off lands as clean components on the Sales Order.
//
// Deliberately SEPARATE from DeliverySelect (John, 7 Aug): DeliverySelect is reused
// by the responder EditAddressModal (itself a modal) + the per-child device picker,
// where a modal-in-a-modal / the strict named flow don't fit. This component is used
// ONLY in TabGeneral, so those contexts are untouched.
//
// Drop-in prop-compatible with DeliverySelect (value / onChange / onPartsChange /
// deliveryAddresses / locked / help); it additionally surfaces the entered name on
// the parts object (parts.name), which resolveDeliveryForSubmit uses as address_name.
// =============================================================================

import { useId, useState } from "react";
import { AddressCaptureModal } from "./AddressCaptureModal.jsx";
import { LockedField } from "./FormUI.jsx";

// The registered-row display string, mirroring DeliverySelect.fmtAddr so a value
// round-trips against the same registered list.
function fmtAddr(a) {
  if (a._fromProfile) return a.street || "";
  return [a.street, a.street2, a.suburb_city || a.suburb, a.state, a.postcode]
    .filter(Boolean)
    .join(", ");
}
function fmtAddrLabel(a) {
  if (a._fromProfile) return a.street || "";
  const addrStr = fmtAddr(a);
  return a.address_name ? `${a.address_name} — ${addrStr}` : addrStr;
}
// Display string for a captured one-off (same slot order as fmtAddr).
function oneOffAddrStr(o) {
  return [o.street, o.street2, o.suburb_city, o.state, o.postcode].filter(Boolean).join(", ");
}

function ShelterDeliverySelect({
  value,
  onChange,
  onPartsChange,
  deliveryAddresses,
  locked,
  help,
}) {
  const radioName = useId();
  const addrs = deliveryAddresses || [];
  const registeredValues = addrs.map(fmtAddr);
  const matchesRegistered = !!value && registeredValues.indexOf(value) !== -1;
  const differentMode = !!value && !matchesRegistered;

  // The structured one-off captured this session (for the card label + Edit prefill).
  // On a draft reopen the parts aren't restored, so this may be null while `value`
  // still holds the one-off string — the card then shows the raw string and Edit opens
  // the modal seeded with the street text.
  const [oneOff, setOneOff] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  if (locked) return <LockedField label="Delivery Address" value={value} wide />;

  function selectRegistered(val) {
    setOneOff(null);
    onChange(val);
    if (onPartsChange) onPartsChange(null);
  }

  function handleModalSave(addr) {
    setOneOff(addr);
    onChange(oneOffAddrStr(addr));
    if (onPartsChange)
      onPartsChange({
        name: addr.name,
        line1: addr.street,
        line2: addr.street2,
        suburb: addr.suburb_city,
        state: addr.state,
        postcode: addr.postcode,
      });
    setModalOpen(false);
  }

  // Seed the modal when editing an already-entered one-off; null for a fresh entry.
  const editInitial = oneOff || (differentMode ? { street: value } : null);

  return (
    <div style={{ gridColumn: "1/-1" }}>
      <div
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 600,
          color: "#607D79",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 5,
        }}
      >
        Delivery Address {help}
      </div>

      {/* Registered addresses as radio-style cards. */}
      {addrs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {addrs.map(function (a, i) {
            const val = fmtAddr(a);
            const isSelected = !differentMode && value === val;
            return (
              <label
                key={a.id || i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "10px 12px",
                  border: isSelected ? "1.5px solid #00897B" : "1.5px solid #e0e8e7",
                  background: isSelected ? "#E0F2F1" : "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <input
                  type="radio"
                  name={radioName}
                  checked={isSelected}
                  onChange={function () {
                    selectRegistered(val);
                  }}
                  style={{ marginTop: 3 }}
                />
                <span style={{ color: "#2C3E3B" }}>{fmtAddrLabel(a)}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* The entered one-off, shown as a selected card with an Edit affordance. */}
      {differentMode ? (
        <div
          style={{
            marginTop: addrs.length > 0 ? 6 : 0,
            padding: "10px 12px",
            border: "1.5px solid #00897B",
            background: "#fffbeb",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <span style={{ color: "#2C3E3B" }}>
              {oneOff?.name ? <strong>{oneOff.name} — </strong> : null}
              {value}
            </span>
            <button
              type="button"
              onClick={function () {
                setModalOpen(true);
              }}
              style={{
                background: "none",
                border: "none",
                color: "#00695C",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
                textDecoration: "underline",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              Edit
            </button>
          </div>
          <div
            style={{
              marginTop: 6,
              padding: "6px 10px",
              background: "#FFF3E0",
              border: "1px solid #FFB300",
              borderRadius: 6,
              fontSize: 12,
              color: "#7c4a00",
            }}
          >
            This address is not registered with Zephyr. A Responder will verify before dispatching.
          </div>
        </div>
      ) : (
        <label
          onClick={function () {
            setModalOpen(true);
          }}
          onKeyDown={function (e) {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setModalOpen(true);
            }
          }}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "10px 12px",
            marginTop: addrs.length > 0 ? 6 : 0,
            border: "1.5px solid #e0e8e7",
            background: "#fff",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          <input type="radio" name={radioName} checked={false} readOnly style={{ marginTop: 3 }} />
          <span style={{ color: "#2C3E3B" }}>
            Different address (enter manually){" "}
            <strong>&mdash; THIS MUST NOT BE THE CLIENT&rsquo;S ADDRESS</strong>
          </span>
        </label>
      )}

      {modalOpen && (
        <AddressCaptureModal
          initial={editInitial}
          onSave={handleModalSave}
          onClose={function () {
            setModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

export { ShelterDeliverySelect };
